import {
  AlignmentDocumentControlNo,
  AmountInfoSettingType,
  CurrencyCode,
  DateOptionType,
  DocumentFontType,
  DocumentFormat,
  DocumentHeaderSize,
  HeaderOptionName,
  HeaderOptionType,
  LanguageType,
  PaymentElementType,
  PositionSummarizeDefault,
  SettingAreaPunchingType,
  SettingCertifiedDate,
} from "@/enums/enums";
import { GetMerchantAddressModelDefault } from "@/helper/addressHelper";
import { GetCredential, GetDefaultPaymentMethodChannel, GetDocumentData } from "@/helper/documentFacadeHelper";
import { DefaultPaymentMethodChannelModel, DocumentDetailModel, OnlineViewModel } from "@/model/document/documentModel";
import { SettingDocumentTemplateModel } from "@/model/document/settingDocumentTemplate";

export function ConvertToBillingNoteOnlineView(detail: any): OnlineViewModel {
  const onlineViewModel: OnlineViewModel = new OnlineViewModel();
  if (detail) {
    const documentDetailModel: DocumentDetailModel = new DocumentDetailModel();
    documentDetailModel.version = detail.document.version;
    documentDetailModel.data = GetDocumentData(detail.document.data);
    documentDetailModel.defaultPaymentMethodChannel = detail.defaultPaymentMethodChannel
      ? GetDefaultPaymentMethodChannel(detail.defaultPaymentMethodChannel)
      : new DefaultPaymentMethodChannelModel();
    onlineViewModel.document = documentDetailModel;
    onlineViewModel.template = detail.template
      ? detail.template.data
      : GetBillingNoteSettingDocument(detail.document.data.currency.toCurrency.currencyCode);
    onlineViewModel.transactionId = detail.transactionId;
    onlineViewModel.transactionType = detail.transactionType;
    onlineViewModel.financeType = detail.financeType;
    onlineViewModel.expenseType = detail.expenseType;
    onlineViewModel.credential = GetCredential(detail.credential);
    onlineViewModel.headerOptionType = detail.headerOptionType;
  }
  return onlineViewModel;
}

export function GetBillingNoteSettingDocument(toCurrency: CurrencyCode): SettingDocumentTemplateModel {
  let settingTemplate = new SettingDocumentTemplateModel();
  settingTemplate = {
    colorCode: "#40CBDE",
    colorCodeCopy: "#40CBDE",
    language: LanguageType.Th,
    areaPunchingType: SettingAreaPunchingType.None,
    isShowWatermarkPaid: false,
    documentFontType: DocumentFontType.Sukhumvit,
    documentFormat: DocumentFormat.FormatOne,
    settingCurrency: toCurrency,
    dateOptionType: DateOptionType.DateADType1,
    documentControlNo: {
      value: "",
      alignment: AlignmentDocumentControlNo.LeftFooter,
    },
    merchantLogo: {
      value: "",
      isShowElement: true,
    },
    isShowEtaxInvoice: true,
    headType: {
      copyAmount: 0,
      originalAmount: 1,
      headerTitleType: HeaderOptionType.Original,
      isShowDocumentSet: false,
      isShowHeaderOptionType: true,
    },
    documentName: {
      headerOptionType: HeaderOptionName.None,
      documentHeaderSize: DocumentHeaderSize.Medium,
      isShowElement: true,
      value: "Billing_Note",
      isShowTaxInvoice: true,
      isShowNonTaxInvoice: true,
    },
    merchant: {
      isShowTextHeadOffice: true,
      merchantAddressTemplate: GetMerchantAddressModelDefault(),
      isCustomTextDisplay: false,
      merchantTitle: {
        value: "Seller",
        isShowElement: true,
      },
      phoneNumber: {
        value: "",
        isShowElement: true,
      },
      email: {
        value: "",
        isShowElement: true,
      },
      website: {
        value: "",
        isShowElement: true,
      },
    },
    contact: {
      isShowContact: true,
      isCustomTextDisplay: false,
      isShowTextHeadOffice: true,
      contactTitle: {
        value: "Customer",
        isShowElement: true,
      },
      isShowContactCode: true,
      dearTitle: {
        value: "Dear",
        isShowElement: true,
      },
      phoneNumber: {
        value: "",
        isShowElement: true,
      },
      email: {
        value: "",
        isShowElement: true,
      },
      website: {
        value: "",
        isShowElement: true,
      },
    },
    documentInfo: {
      documentNoTitle: {
        value: "Doc_No",
        isShowElement: true,
      },
      issedDateTitle: {
        value: "Issue_Date",
        isShowElement: true,
      },
      creditTitle: {
        value: "Credit_text",
        isShowElement: false,
      },
      acceptedDateTitle: {
        value: "Accect_Date",
        isShowElement: false,
      },
      dueDateTitle: {
        value: "Due_Date",
        isShowElement: true,
      },
      refTitle: {
        value: "Ref",
        isShowElement: true,
      },
      taxInvoiceNumberTitle: {
        value: "TaxInvoice_No",
        isShowElement: false,
      },
      taxInvoiceDateTimeTitle: {
        value: "TaxInvoice_Date",
        isShowElement: false,
      },
      dateOptionType: DateOptionType.DateADType1,
      isCustomTextDisplay: false,
      isShowIssueDateValue: true,
    },
    merchantPerson: {
      isShowMerchantPerson: true,
      isCustomTextDisplay: false,
      merchantTitle: {
        value: "",
        isShowElement: true,
      },
      merchantPersonTitle: {
        value: "Contact_Back_At",
        isShowElement: true,
      },
      phoneNumber: {
        value: "",
        isShowElement: true,
      },
      email: {
        value: "",
        isShowElement: true,
      },
    },
    settingColumn: {
      isShowAmount: true,
      isShowUnit: false,
      isShowVatAmount: true,
      isShowPreVatAmount: true,
      isShowIncVatAmount: false,
      isShowWhtAmount: false,
      isShowBilledAmount: true,
      isShowDocumentNumber: true,
      isShowDueDate: true,
      isShowIssedDate: true,
      isShowDiscount: true,
      isShowPrice: true,
      isShowProductItems: false,
    },
    settingDescription: {
      isShowCode: true,
      isShowDescription: true,
      isShowImage: true,
      isShowName: true,
    },
    amountInfo: {
      amountInfoSettingType: AmountInfoSettingType.NotDisplayCaseZero,
      isCustomTextDisplay: false,
      discountTitle: {
        value: "Total_Discount",
        isShowElement: true,
      },
      netAmountTitle: {
        value: "Total_Amount",
        isShowElement: true,
      },
      nonVATAmountTitle: {
        value: "VAT_Exempted_Amount",
        isShowElement: true,
      },
      preVATAmountTitle: {
        value: "VAT_7_Amount",
        isShowElement: true,
      },
      vatAmountTitle: {
        value: "VAT_Amount",
        isShowElement: true,
      },
      zeroVATAmountTitle: {
        value: "VAT_0_Amount",
        isShowElement: true,
      },
      totalDocumentAmountAfterTitle: {
        value: "Total_Amount_Tax_invoice",
        isShowElement: false,
      },
      referenceTransactionAmountTitle: {
        value: "New_amount",
        isShowElement: false,
      },
    },
    summarizeInfo: {
      isShowSummary: true,
      isCustomTextDisplay: false,
      positionSummarizeDefault: PositionSummarizeDefault.SummaryAmount,
      summaryAmountTitle: {
        value: "Total_Amount",
        isShowElement: true,
      },
      paymentAmountTitle: {
        value: "Payment_Amount_Income",
        isShowElement: true,
      },
      whtAmountTitle: {
        value: "WHT_Amount",
        isShowElement: true,
      },
    },
    merchantPayment: {
      paymentElementType: PaymentElementType.List,
      settingPaymentGroupList: {
        isShowPaymentGroupList: true,
        isShowPaymentGroupAll: false,
        isShowBankAccountBranchName: true,
        isShowBankAccountName: true,
        isShowBankAccountNumber: true,
      },
      settingPaymentMethodChannel: {
        isShowBankAccountBranchCode: true,
        isShowBankAccountBranchName: true,
        isShowBankAccountName: true,
        isShowBankAccountNumber: true,
      },
    },
    remark: {
      value: "",
      isShowElement: true,
    },
    reason: {
      value: "",
      isShowElement: false,
    },
    expenseClaimDetail: {
      idCard: {
        value: "",
        isShowElement: false,
      },
      expenseClaimNote: {
        value: "",
        isShowElement: false,
      },
    },
    certified: {
      userCreateTitle: {
        value: "Created_By_Seller",
        isShowElement: true,
      },
      userApproveTitle: {
        value: "Approved_By_Seller",
        isShowElement: true,
      },
      merchantStampTitle: {
        value: "Stamp_Seller",
        isShowElement: true,
      },
      receivedByTitle: {
        value: "Received_By_Customer",
        isShowElement: true,
      },
      stampTitle: {
        value: "Stamp_Customer",
        isShowElement: true,
      },
      isShowUserCreateUrl: true,
      isShowUserApproveUrl: true,
      isShowUserCreateDate: true,
      isShowUserApproveDate: true,
      isShowUserApproveName: true,
      isShowUserCreateName: true,
      isShowMerchantStampUrl: true,
      isCustomTextDisplay: false,
      isShowQRCode: true,
      settingCreateDate: SettingCertifiedDate.ByDefaultDate,
      settingApproveDate: SettingCertifiedDate.ByDefaultDate,
    },
  };

  return settingTemplate;
}
